import React, { useState, useEffect, useRef } from 'react';
import './EmailPreviewModal.css';

function EmailPreviewModal({
  isOpen,
  emailData,      // { subject, body, to, from_email }
  onSend,         // (editedSubject, editedBody) => void
  onCancel,
  emailType,      // 'job_offer', 'documents', 'contract', 'induction', 'outcome'
  title = 'Review Email Before Sending',
  sendLabel = 'Send Email',
  // Optional third button between Cancel and Send — the rejection email offers
  // "Don't send an email": the status is already set, so this is a real choice,
  // not a cancel. { label, onClick }.
  secondaryAction = null,
  cancelLabel = 'Cancel',
  note = null     // one line under the From/To block, e.g. who this sends as
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // True from the first click on Send until the caller's onSend settles. The
  // button was never disabled, so a second click during the ~20-60s a send takes
  // queued a second email (candidate 19 received the documents pack twice,
  // 2026-07-23). The backend now refuses a dispatch while one is in flight too;
  // this just stops the obvious double click at the source.
  const [sending, setSending] = useState(false);
  const savedScrollPosition = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (isOpen && emailData) {
      setSubject(emailData.subject || '');
      setBody(emailData.body || '');
    }
    if (!isOpen) setSending(false);
  }, [isOpen, emailData]);

  useEffect(() => {
    if (isOpen) {
      savedScrollPosition.current = {
        x: window.scrollX || window.pageXOffset,
        y: window.scrollY || window.pageYOffset
      };
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      requestAnimationFrame(() => {
        window.scrollTo(savedScrollPosition.current.x, savedScrollPosition.current.y);
      });
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      await onSend(subject, body);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="email-preview-overlay" onClick={onCancel} />
      <div className="email-preview-modal">
        <h2>{title}</h2>

        <div className="email-preview-info">
          <p><strong>To:</strong> {emailData?.to}</p>
          <p><strong>From:</strong> {emailData?.from_email}</p>
          {note && <p className="email-preview-note">{note}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="email-subject">Subject:</label>
          <input
            id="email-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="email-subject-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="email-body">Message:</label>
          <textarea
            id="email-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="email-body-textarea"
            rows={15}
          />
        </div>

        <div className="email-preview-buttons">
          <button type="button" className="btn-cancel" onClick={onCancel}>
            {cancelLabel}
          </button>
          {secondaryAction && (
            <button type="button" className="btn-cancel" onClick={secondaryAction.onClick} disabled={sending}>
              {secondaryAction.label}
            </button>
          )}
          <button type="button" className="btn-send" onClick={handleSend} disabled={sending}>
            {sending ? 'Sending…' : sendLabel}
          </button>
        </div>
      </div>
    </>
  );
}

export default EmailPreviewModal;
